import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button, Input, Fieldset, Legend, Text, Heading } from '../components/ui'
import { ArrowLeftIcon } from '@heroicons/react/16/solid'
import { BRAND_COLOR } from '../constants'
import { useTheme } from '../hooks'
import { useAuthContext } from '../contexts/AuthContext'

const ForgotPassword: React.FC = () => {
  const { darkMode } = useTheme()
  const navigate = useNavigate()
  const { resetPassword, loading, error, isAuthenticated } = useAuthContext()
  const [forgotError, setForgotError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Redirecionar usuários já logados
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)
    
    const result = await resetPassword(email)
    
    if (result.success) {
      setIsSubmitted(true)
    } else {
      setForgotError(result.error || 'Erro ao enviar e-mail de recuperação')
      setSubmitError(result.error || 'Erro ao enviar e-mail de recuperação')
    }
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img 
            src="/logo-pasi.svg" 
            alt="PASI Logo" 
            className="w-12 h-12 rounded-lg object-contain"
          />
        </div>
        <Heading as="h2" size="2xl" className="mt-6 text-center">
          Recuperar senha
        </Heading>
        <Text size="sm" color="muted" className="mt-2 text-center">
          Digite seu e-mail para receber instruções de recuperação
        </Text>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {!isSubmitted ? (
            <>
                        {(forgotError || submitError) && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {submitError || forgotError}
            </div>
          )}
              
              <form className="space-y-6" onSubmit={handleSubmit}>
                <Fieldset>
                  <Legend>Endereço de e-mail</Legend>
                  <div className="mt-2">
                    <Input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Digite seu e-mail"
                      required
                      disabled={loading}
                    />
                  </div>
                </Fieldset>

                <div>
                  <Button 
                    type="submit" 
                    className="w-full text-white" 
                    size="lg"
                    style={{ backgroundColor: BRAND_COLOR }}
                    onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.backgroundColor = '#006d75'
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.backgroundColor = BRAND_COLOR
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Enviando...' : 'Enviar instruções'}
                  </Button>
                </div>

                <div className="text-center space-y-2">
                  <button 
                    type="button"
                    onClick={handleBackToLogin}
                    className="inline-flex items-center text-sm font-medium"
                    style={{ color: BRAND_COLOR }}
                    onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.color = '#006d75'
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.color = BRAND_COLOR
                    }}
                    disabled={loading}
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Voltar para o login
                  </button>
                  
                  <div className="pt-2 border-t border-gray-200">
                    <Link 
                      to="/test-reset"
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Testar diferentes formatos de URL de reset
                    </Link>
                  </div>
                </div>
              </form>
            </>
          ) : (
            <div className="space-y-6 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <div>
                <Heading as="h3" size="2xl" className="text-green-600 dark:text-green-400">
                  E-mail enviado!
                </Heading>
                <Text size="sm" color="muted" className="mt-2">
                  Enviamos instruções de recuperação para <strong>{email}</strong>
                </Text>
                <Text size="sm" color="muted" className="mt-2">
                  Clique no link do e-mail para redefinir sua senha.
                </Text>
              </div>

              <div className="space-y-3">
                <Text size="sm" color="muted">
                  Não recebeu o e-mail? Verifique sua pasta de spam ou
                </Text>
                <Button 
                  type="button"
                  onClick={() => setIsSubmitted(false)}
                  className="w-full text-white" 
                  size="lg"
                  style={{ backgroundColor: BRAND_COLOR }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.backgroundColor = '#006d75'
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.backgroundColor = BRAND_COLOR
                  }}
                >
                  Tentar novamente
                </Button>
                
                <button 
                  type="button"
                  onClick={handleBackToLogin}
                  className="inline-flex items-center text-sm font-medium"
                  style={{ color: BRAND_COLOR }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.color = '#006d75'
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.color = BRAND_COLOR
                  }}
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Voltar para o login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword
